import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../mendepartmentdetails/Sidebar';
import SubDeptImage from '../../../assets/image/department/license-renewal.jpg';

function LicenseRenewalDetails() {
  const [Faq, setFaq] = useState(0); // first dropdown open by default
  const titleRef = useRef(null);
  const [titleColor, setTitleColor] = useState('');

  useEffect(() => {
    if (titleRef.current) {
      const c = window.getComputedStyle(titleRef.current).color;
      setTitleColor(c);
    }
  }, []);

  // stacked layout so long titles wrap cleanly
  const liRow = { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' };
  const icon = { marginTop: '4px', flex: '0 0 auto' };
  const kv = { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 };
  const keyText = { fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.35, color: '#0f172a' };
  const valText = { fontSize: '1rem', lineHeight: 1.6, color: '#334155' };
  const overviewText = { color: titleColor || '#0f172a', fontSize: '1.05rem', lineHeight: 1.6, margin: '8px 0 16px' };

  const sections = [
    {
      Que: 'How You Can Renew',
      Answer: (
        <ul className="list-unstyled list-style">
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Online or by Mail (if eligible):</span>
              <span style={valText}>
                If you have a current valid photo license, you may be eligible to renew online or by mail. Your renewal
                notice explains your options.
              </span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>In Person:</span>
              <span style={valText}>
                Required if you renewed by mail/online last time, have certain license types, or are directed to appear
                for a new photo.
              </span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>60+ Non-Photo Option:</span>
              <span style={valText}>
                Drivers age 60+ may choose a non-photo license by mail/online; consider a photo license if you travel.
              </span>
            </div>
          </li>
        </ul>
      ),
    },
    {
      Que: 'When to Renew & Late Fees',
      Answer: (
        <ul className="list-unstyled list-style">
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Renew before expiration:</span>
              <span style={valText}>
                There’s no grace period. If it’s expired &gt; 30 days, a $5 late fee applies; &gt; 6 months, $10. If you
                wait 5 years, you’ll owe late fees and must pass vision, knowledge, and road tests again.
              </span>
            </div>
          </li>
        </ul>
      ),
    },
    {
      Que: 'What to Bring (In-Person)',
      Answer: (
        <ul className="list-unstyled list-style">
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Identity & Legal Presence (first renewal since 1/1/2001):</span>
              <span style={valText}>
                Bring proof of U.S. citizenship or lawful permanent residency. You may be required to renew in person.
              </span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Standard documents:</span>
              <span style={valText}>
                Primary ID, secondary ID, any name-change proof, and two proofs of state residency (no photocopies).
              </span>
            </div>
          </li>
        </ul>
      ),
    },
    {
      Que: 'REAL ID at Renewal (Optional)',
      Answer: (
        <>
          <p style={valText}>
            You do not need REAL ID to drive, vote, or access many services. From May 7, 2025, REAL ID or a valid
            passport is required to board domestic flights and access certain federal facilities. Getting REAL ID during
            renewal costs the standard renewal fee.
          </p>
          <ul className="list-unstyled list-style" style={{ marginBottom: 0 }}>
            <li style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>Bring required documents:</span>
                <span style={valText}>
                  Identity, Social Security, and two proofs of residency to upgrade to REAL ID.
                </span>
              </div>
            </li>
          </ul>
        </>
      ),
    },
    {
      Que: 'Fees at a Glance',
      Answer: (
        <ul className="list-unstyled list-style">
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Standard Class D (8-year cycle):</span>
              <span style={valText}>$28</span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>For-Hire endorsement add-on:</span>
              <span style={valText}>$4.50</span>
            </div>
          </li>
        </ul>
      ),
    },
    {
      Que: 'After You Renew',
      Answer: (
        <ul className="list-unstyled list-style">
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Interim License:</span>
              <span style={valText}>
                You may receive an interim paper license valid for up to 20 days; your permanent card is mailed to you.
              </span>
            </div>
          </li>
        </ul>
      ),
    },
    {
      Que: 'Special Situations',
      Answer: (
        <ul className="list-unstyled list-style">
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Military:</span>
              <span style={valText}>
                If stationed outside the state, you can add “Code 30” (license does not expire while on active duty).
                After separation or reassignment back in-state, renew within 60 days.
              </span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Address Change:</span>
              <span style={valText}>
                Update your address within 10 days. You don’t have to reprint your license, but your file must be
                current.
              </span>
            </div>
          </li>
        </ul>
      ),
    },
    {
      Que: 'Driver Service Centers',
      Answer: <p style={valText}>Typical hours: 8:30 AM – 5:00 PM, closed on state holidays. Expect higher volume after holidays.</p>,
    },
  ];

  return (
    <section className="department-details-section">
      <div className="container">
        <div className="row">

          {/* Sidebar */}
          <div className="col-12 col-lg-4 col-xl-4">
            <Sidebar />
          </div>

          {/* Main Content */}
          <div className="col-lg-8">
            <div className="department-details-imgbox">
              <img src={SubDeptImage} className="img-fluid" alt="Driver’s License Renewal" />
            </div>

            <div className="department-details-content-box">
              <h4 ref={titleRef} className="department-details-title">Driver’s License Renewal</h4>

              {/* Overview (always visible, same color as title but smaller) */}
              <p style={overviewText}>
                Renew on time to avoid late fees or re-testing. You can renew online (if eligible), by mail (if eligible),
                or in person at a Driver Service Center.
              </p>

              {/* Actions: keep fees PDF, move applications to Forms hub */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', margin: '0 0 16px' }}>
                <a 
                  href="/guides/StateofGTSDL-Manual.pdf" 
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    fontSize: '14px',
                    color: '#e75934',
                    textDecoration: 'none',
                    border: '1px solid #e75934',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease',
                    backgroundColor: 'transparent',
                    width: 'fit-content'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#e75934';
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#e75934';
                  }}
                >
                  <i className="fa-solid fa-file-pdf" style={{ marginRight: '8px', fontSize: '14px' }} />
                  Download Driver's License Renewal Guide (PDF)
                </a>
                <Link
                  to="/forms"
                  className="btn btn-secondary d-inline-flex align-items-center justify-content-center"
                >
                  Registration/Renewal (Online Forms)
                </Link>
              </div>
            </div>

            {/* Accordion (other sections) */}
            <div className="accordian-box">
              {sections.map((item, index) => (
                <div className={`accordian-box-item ${Faq !== index && 'active'}`} key={index}>
                  <div className="accordian-title">
                    <h5>{item.Que}</h5>
                    <i
                      className="fa-solid fa-angle-right"
                      onClick={() => setFaq(index)}
                      role="button"
                      aria-label={`Toggle ${item.Que}`}
                    />
                  </div>

                  {Faq === index && (
                    <div className="accordian-content" style={{ display: 'block' }}>
                      {item.Answer}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div style={{ marginTop: 12 }}>
              <Link to="/department/dmv">
                <i className="fa-solid fa-circle-arrow-left" style={{ marginRight: '5px' }} />
                Go Back
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default LicenseRenewalDetails;
